/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../../../logging/Log";
import type { IPlayer } from "../../interfaces";
import { Direction, SpeedLevel, TrickPlayMode } from "../../enums";
import { SPEED_VALUE } from "../../types";
import type { ITrickPlayControl, ITrickPlayConfig } from "../../interfaces";

/**
 * Thumbnail trick-play implementation.
 *
 * Used when `enableThumbnailSeek = true` AND a TMIS thumbnail service
 * is reachable. The player remains paused at the seek entry point
 * during the session (TP-SP-8). This impl tracks a virtual target
 * position that the UI uses to show thumbnail previews via
 * ThumbnailManager. On confirm, TrickPlayManager commits the seek
 * to the target position.
 *
 * During scrub, this impl is a no-op on move (UI renders preview
 * directly from ThumbnailManager); only scrubEnd commits the seek.
 */
export class ThumbnailTrickplayControlImpl implements ITrickPlayControl {
  private static readonly TAG: string = "ThumbnailTrickplayControlImpl";

  public readonly mode: TrickPlayMode = TrickPlayMode.Thumbnail;

  /** Reference to the active player instance. */
  private mPlayer: IPlayer;

  /** Trick-play configuration values. */
  private mConfig: ITrickPlayConfig;

  /** Handle for the active setInterval tick, or null if idle. */
  private mTickId: ReturnType<typeof setInterval> | null = null;

  /**
   * Virtual seek target tracked during an active session.
   * Player stays at entry point; this represents where confirm would seek.
   * Null when no session is active.
   */
  private mTargetPosition: number | null = null;

  /**
   * Optional override for the backward clamp limit, in place of the
   * seekable range start. Supplied by TrickPlayManager so the seek motion
   * can continue past the live buffer start into the Start Over stream.
   */
  private readonly mGetStartOverLowerBound: (() => number) | undefined;

  constructor(
    player: IPlayer,
    config: ITrickPlayConfig,
    getStartOverLowerBound?: () => number
  ) {
    this.mPlayer = player;
    this.mConfig = config;
    this.mGetStartOverLowerBound = getStartOverLowerBound;
  }

  // ── Private Methods ──────────────────────────────────────────────────────

  private startTick(fn: () => void, ms: number): void {
    this.clearTick();
    this.mTickId = setInterval(fn, ms);
    fn();
  }

  private clearTick(): void {
    if (this.mTickId !== null) {
      clearInterval(this.mTickId);
      this.mTickId = null;
    }
  }

  /**
   * Initialize mTargetPosition from the player if not already tracking.
   */
  private initTargetPosition(): void {
    if (this.mTargetPosition === null) {
      this.mTargetPosition = this.mPlayer.getCurrentTime();
      this.mPlayer.pause();
    }
  }

  /**
   * Resolve the backward clamp limit: the injected Start Over lower bound
   * when supplied and earlier than the buffer start, otherwise the
   * buffer start itself.
   *
   * @param bufferStart - The seekable range start (live buffer start).
   * @returns The effective backward clamp limit.
   */
  private resolveLowerBound(bufferStart: number): number {
    if (this.mGetStartOverLowerBound === undefined) {
      return bufferStart;
    }
    const bound = this.mGetStartOverLowerBound();
    return Math.min(bound, bufferStart);
  }

  /**
   * Clamp mTargetPosition to the seekable range (TP-SK-4, TP-SK-5).
   */
  private clampTargetPosition(): void {
    if (this.mTargetPosition === null) {
      return;
    }
    const range = this.mPlayer.getSeekableRange();
    const lowerBound = this.resolveLowerBound(range.start);
    if (this.mTargetPosition <= lowerBound) {
      this.mTargetPosition = lowerBound;
      this.clearTick();
    }
    if (this.mTargetPosition >= range.end) {
      this.mTargetPosition = range.end;
      this.clearTick();
    }
  }

  /**
   * Clamp a target position to the seekable range boundaries.
   * If the target exceeds a boundary, the tick is cleared (auto-stop).
   *
   * @returns The clamped position.
   */
  private clampToSeekRange(target: number): number {
    const range = this.mPlayer.getSeekableRange();
    const lowerBound = this.resolveLowerBound(range.start);
    if (target <= lowerBound) {
      this.clearTick();
      return lowerBound;
    }
    if (target >= range.end) {
      this.clearTick();
      return range.end;
    }
    return target;
  }

  // ── ITrickPlayControl ────────────────────────────────────────────────────y

  public skip(direction: Direction): void {
    const step =
      direction === Direction.Forward
        ? this.mConfig.forwardStep
        : this.mConfig.backwardStep;
    this.initTargetPosition();
    const target = this.clampToSeekRange(
      (this.mTargetPosition as number) + direction * step
    );
    this.mTargetPosition = target;
    Log.d(
      ThumbnailTrickplayControlImpl.TAG,
      "skip() direction=" + direction + " target=" + target
    );
  }

  public fastForward(direction: Direction, level: SpeedLevel): void {
    this.initTargetPosition();
    const delta = SPEED_VALUE[level] * direction;
    const intervalMs = this.mConfig.tickIntervalMs;
    Log.d(
      ThumbnailTrickplayControlImpl.TAG,
      "fastForward() direction=" +
        direction +
        " level=" +
        level +
        " delta=" +
        delta
    );
    this.startTick(() => {
      this.mTargetPosition = (this.mTargetPosition as number) + delta;
      this.clampTargetPosition();
    }, intervalMs);
  }

  public onScrubBegin(_positionSec: number): void {
    // No-op: UI handles thumbnail preview via ThumbnailManager.
  }

  public onScrubMove(_positionSec: number): void {
    // No-op: UI renders preview via ThumbnailManager.getThumbnail(pos).
    // No player call needed during thumbnail-mode scrub move.
  }

  public onScrubEnd(positionSec: number): void {
    this.mPlayer.seek(positionSec);
    Log.d(ThumbnailTrickplayControlImpl.TAG, "onScrubEnd() pos=" + positionSec);
  }

  public getTargetPosition(): number {
    return this.mTargetPosition !== null
      ? this.mTargetPosition
      : this.mPlayer.getCurrentTime();
  }

  public pauseScrubbing(): void {
    this.clearTick();
    // Keep mTargetPosition so getTargetPosition() still returns
    // the last-reached virtual target during the hold-release wait.
    Log.d(ThumbnailTrickplayControlImpl.TAG, "pauseScrubbing()");
  }

  public stopScrubbing(): void {
    this.clearTick();
    this.mTargetPosition = null;
    Log.d(ThumbnailTrickplayControlImpl.TAG, "stopScrubbing()");
  }
}
